import React, { useState, useMemo, useCallback } from 'react';
import allServers, { MCPServer } from './data';
import { useClientEvent } from '@app/hooks/useClientEvent';

type ConfigTarget = 'vscode' | 'cursor' | 'config';

const FILTER_CATEGORIES = ['All', 'CI/CD', 'DevOps', 'Testing'] as const;

const CONFIG_TITLES: Record<ConfigTarget, string> = {
  vscode: 'Add to VS Code',
  cursor: 'Add to Cursor',
  config: 'Raw Configuration',
};

const CONFIG_INSTRUCTIONS: Record<ConfigTarget, string> = {
  vscode: 'Copy the configuration below and add it to your VS Code settings.json:',
  cursor: 'Copy the configuration below and add it to your Cursor .cursor/mcp.json:',
  config: 'Copy the raw MCP server configuration:',
};

export const MCPCatalog: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [modalMcp, setModalMcp] = useState<MCPServer | null>(null);
  const [modalTarget, setModalTarget] = useState<ConfigTarget>('vscode');
  const [copied, setCopied] = useState(false);
  const fireEvent = useClientEvent();

  const servers = allServers;

  const filtered = useMemo(() => {
    return servers.filter((s) => {
      const matchesSearch =
        !search ||
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.hint.toLowerCase().includes(search.toLowerCase()) ||
        s.description.toLowerCase().includes(search.toLowerCase()) ||
        s.topTools.some((t) => t.toLowerCase().includes(search.toLowerCase()));
      const matchesFilter = activeFilter === 'All' || s.category === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [servers, search, activeFilter]);

  const openConfig = useCallback((mcp: MCPServer, target: ConfigTarget) => {
    setModalMcp(mcp);
    setModalTarget(target);
    setCopied(false);
    fireEvent('MCP_SERVICE_CONFIG_OPENED', { serviceName: mcp.name, target });
  }, [fireEvent]);

  const [copyError, setCopyError] = useState(false);

  const copyToClipboard = useCallback(() => {
    if (!modalMcp) return;
    const text = modalMcp.configs[modalTarget];
    const onSuccess = () => {
      setCopied(true);
      setCopyError(false);
      setTimeout(() => setCopied(false), 2000);
    };
    const onFailure = () => {
      setCopyError(true);
      setTimeout(() => setCopyError(false), 3000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess, onFailure);
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy') ? onSuccess() : onFailure();
        document.body.removeChild(ta);
      } catch {
        onFailure();
      }
    }
  }, [modalMcp, modalTarget]);

  const renderDescription = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) =>
      urlRegex.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="config-note-link">
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  return (
    <div className="mcp-catalog-page">
      <div className="page-header">
        <h1>🗂️ MCP Catalog</h1>
        <p>Browse available MCP servers and add them to your favorite IDE</p>
      </div>

      <div className="catalog-search">
        <input
          type="text"
          className="catalog-search-input"
          placeholder="🔍 Search MCPs..."
          aria-label="Search MCP catalog"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="filter-tabs">
          {FILTER_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`filter-tab${activeFilter === cat ? ' active' : ''}`}
              onClick={() => setActiveFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="mcp-catalog-grid">
        {filtered.map((mcp) => (
          <div key={mcp.id} className="mcp-catalog-card">
            <div className="mcp-catalog-header">
              <div className="mcp-catalog-icon" style={{ background: mcp.iconBg }}>
                {mcp.icon}
              </div>
                <div className="mcp-catalog-info">
                  <h3>{mcp.name}</h3>
                  <p className="mcp-catalog-hint">{mcp.hint}</p>
                </div>
            </div>
            <div className="mcp-catalog-tools">
              <span className="tool-count">{mcp.toolCount} tools</span>
              {mcp.topTools.map((tool) => (
                <span key={tool} className="tool-tag-small">{tool}</span>
              ))}
              {mcp.toolCount - mcp.topTools.length > 0 && (
                <span className="tool-tag-small">+{mcp.toolCount - mcp.topTools.length}</span>
              )}
            </div>
            <div className="mcp-catalog-actions">
              <button className="ide-btn vscode-btn" onClick={() => openConfig(mcp, 'vscode')}>
                <span className="ide-icon">💻</span> VS Code
              </button>
              <button className="ide-btn cursor-btn" onClick={() => openConfig(mcp, 'cursor')}>
                <span className="ide-icon">⚡</span> Cursor
              </button>
              <button className="ide-btn config-btn" onClick={() => openConfig(mcp, 'config')}>
                <span className="ide-icon">📋</span> Config
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="mcp-catalog-empty">
            No MCP servers match your search. Try a different query or filter.
          </div>
        )}
      </div>

      {modalMcp && (
        <div className="config-modal-overlay active" onClick={() => setModalMcp(null)}>
          <div className="config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="config-modal-header">
              <h2>{CONFIG_TITLES[modalTarget]}</h2>
              <button className="config-modal-close" onClick={() => setModalMcp(null)}>×</button>
            </div>
            <div className="config-modal-body">
              <p className="config-instructions">{CONFIG_INSTRUCTIONS[modalTarget]}</p>
              <div className="config-code-block">
                <pre>{modalMcp.configs[modalTarget]}</pre>
                <button className="copy-btn" onClick={copyToClipboard}>
                  {copied ? '✅ Copied!' : copyError ? '❌ Copy failed' : '📋 Copy'}
                </button>
              </div>
              {modalMcp.description && (
                <div className="config-note">
                  <strong>📌 Note:</strong> Replace placeholder values with your actual credentials.
                  <div className="config-note-description">
                    <strong>🔑 How to get your token / credentials:</strong>
                    <p>{renderDescription(modalMcp.description)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
